import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BoltIcon, Squares2X2Icon, Battery50Icon,
  AdjustmentsHorizontalIcon, ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { jestPolska } from '../kraj'
import IkonaAsystenta from './ikony/IkonaAsystenta'
import IkonaDoboruLadowarki from './ikony/IkonaDoboruLadowarki'

/**
 * Rozwijana lista asystentów doboru w pasku nawigacji.
 *
 * ═══ PO CO ═══
 * Asystentów mamy pięciu, a do paska trafił jeden. Pozostałe cztery dało się
 * otworzyć wyłącznie z przycisku na stronie kategorii albo z jednego odnośnika
 * w sekcji powitalnej — kto raz z niej zszedł, nie miał jak do nich wrócić.
 *
 * ═══ DWIE GRUPY, BO TO DWIE RÓŻNE DECYZJE ═══
 * „Dobierz instalację" to całość od zera, „Dobierz komponent" to jedna rzecz do
 * układanki, którą ktoś już ma. Nagłówki grup zdejmują z etykiet obowiązek
 * tłumaczenia różnicy: pozycje mogą nazywać się krótko, bo grupa mówi resztę.
 *
 * ═══ NAZWY W PIERWSZEJ OSOBIE ═══
 * „Na podstawie parametrów" i „na podstawie pytań praktycznych" opisywały NASZ
 * mechanizm. „Podaję parametry" i „Odpowiadam na pytania" opisują to, co robi
 * człowiek, a to jest jedyne, co go w tej chwili interesuje. Te same dwie
 * etykiety stoją w przełączniku wewnątrz obu asystentów
 * (components/kreator/WyborDrogi.jsx), więc kto raz je przeczyta, rozpozna je
 * wszędzie.
 */
const GRUPY = [
  {
    tytul: 'menu.dobierz_instalacje',
    pozycje: [
      {
        to: '/dobor-zestawu-pv', Icon: AdjustmentsHorizontalIcon,
        label: 'menu.podaje_parametry',
        opis: 'menu.podaje_parametry_opis',
      },
      {
        to: '/dobierz-zestaw', Icon: ChatBubbleLeftRightIcon,
        label: 'menu.odpowiadam',
        opis: 'menu.odpowiadam_opis',
      },
    ],
  },
  {
    tytul: 'menu.dobierz_komponent',
    pozycje: [
      { to: '/dobor-modulow', Icon: Squares2X2Icon, label: 'menu.moduly_pv' },
      { to: '/dobor-falownika', Icon: BoltIcon, label: 'menu.falownik' },
      { to: '/dobor-magazynu', Icon: Battery50Icon, label: 'menu.magazyn_energii' },
      { to: '/dobor-ladowarki', Icon: IkonaDoboruLadowarki, label: 'menu.ladowarka' },
    ],
  },
]

const MenuAsystentow = () => {
  const { t } = useTranslation('wspolne')
  const navigate = useNavigate()
  const [otwarte, setOtwarte] = useState(false)
  const ref = useRef(null)

  // Zamykanie kliknięciem poza menu i klawiszem Escape — ta sama mechanika co
  // w `UserMenu`. Bez tego menu zostaje otwarte po przejściu na inną stronę.
  useEffect(() => {
    if (!otwarte) return undefined
    const pozaMenu = (e) => { if (ref.current && !ref.current.contains(e.target)) setOtwarte(false) }
    const escape = (e) => { if (e.key === 'Escape') setOtwarte(false) }
    document.addEventListener('mousedown', pozaMenu)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', pozaMenu)
      document.removeEventListener('keydown', escape)
    }
  }, [otwarte])

  const idz = (to) => { setOtwarte(false); navigate(to) }

  return (
    <div className='relative' ref={ref}>
      <button
        type='button'
        onClick={() => setOtwarte((o) => !o)}
        title={t('menu.asystent_doboru')}
        aria-label={t('menu.asystent_doboru')}
        aria-haspopup='menu'
        aria-expanded={otwarte}
        /* Jedyna NIEBIESKA zakładka w pasku poza „Zostań partnerem": asystent
           jest tym, po co ludzie tu wchodzą, a wśród szarych zakładek ginął. */
        className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm font-semibold
                    transition-colors ${
          otwarte ? 'text-white bg-blue-700' : 'text-white bg-blue-600 hover:bg-blue-500'
        }`}
      >
        <IkonaAsystenta className='w-5 h-5 flex-shrink-0' />
        <span className='hidden pasek:inline whitespace-nowrap'>{t('menu.asystent_doboru')}</span>
      </button>

      {otwarte && (
        <div
          role='menu'
          className='absolute right-0 mt-1 w-72 rounded-xl border border-gray-200 bg-white shadow-lg py-1.5 z-50'
        >
          {/* Asystenci pojedynczych komponentów są jeszcze tylko po polsku. */}
          {(jestPolska ? GRUPY : GRUPY.slice(0, 1)).map((grupa, gi) => (
            <React.Fragment key={grupa.tytul}>
              {gi > 0 && <div className='my-1.5 border-t border-gray-100' />}
              {/* Nagłówek grupy WYŚRODKOWANY i w kolorze marki: to jedyne dwa
                  napisy w tym menu, które nie są klikalne, więc muszą wyglądać
                  inaczej niż pozycje, a nie tylko mniej. */}
              <p className='px-3 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wide text-blue-600 text-center'>
                {t(grupa.tytul)}
              </p>
              {grupa.pozycje.map(({ to, Icon, label, opis }) => (
                <button
                  key={to} type='button' role='menuitem' onClick={() => idz(to)}
                  className='w-full flex items-start gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors'
                >
                  <Icon className='w-5 h-5 mt-0.5 text-gray-400 flex-shrink-0' />
                  <span className='min-w-0'>
                    <span className='block text-sm font-medium text-gray-900'>{t(label)}</span>
                    {opis && <span className='block text-xs text-gray-500 mt-0.5'>{t(opis)}</span>}
                  </span>
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  )
}

export default MenuAsystentow
